import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getFacilityInfo } from '../../services/facilityService';
import styles from './PublicReservationForm.module.css';

const PublicReservationForm = ({ selectedDates = [], onClose, onSuccess, isInline = false }) => {
    const [formData, setFormData] = useState({
        guestName: '',
        guestEmail: '',
        guestPhone: '',
        eventName: '',
        eventDescription: '',
        groupDescription: '',
        purposeOfVisit: '',
        adultCount: 0,
        childCount: 0,
        responsiblePerson: '',
        startTime: '',
        endTime: '',
        accountId: ''
    });

    const [accounts, setAccounts] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [expiryHours, setExpiryHours] = useState(48);

    // Update startTime and endTime based on selectedDates
    useEffect(() => {
        if (selectedDates && selectedDates.length > 0) {
            const sortedDates = [...selectedDates].sort();
            const start = sortedDates[0];
            const end = sortedDates[sortedDates.length - 1];

            setFormData(prev => ({
                ...prev,
                startTime: `${start}T09:00`,
                endTime: `${end}T17:00`
            }));
        }
    }, [selectedDates]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [accountsRes, facilityRes] = await Promise.all([
                    axios.get('/api/account/public-list'),
                    getFacilityInfo()
                ]);
                setAccounts(accountsRes.data);
                if (facilityRes?.pendingExpiryHours) setExpiryHours(facilityRes.pendingExpiryHours);
            } catch (err) {
                console.error("Failed to load form data", err);
                setError("Failed to load list of churches/organizations.");
            }
        };
        fetchData();
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setError(null);

        try {
            // Auto-fill eventName if empty (since we hid it/removed it from main view but backend likely needs it)
            const submissionData = { ...formData };
            if (!submissionData.eventName || submissionData.eventName.trim() === '') {
                submissionData.eventName = submissionData.groupDescription
                    ? `${submissionData.groupDescription} Reservation`
                    : `Reservation for ${submissionData.guestName}`;
            }

            // Basic client-side validation could go here
            await axios.post('/api/reservations/public', {
                ...submissionData,
                accountId: parseInt(submissionData.accountId)
            });
            onSuccess();
        } catch (err) {
            console.error("Submission error", err);
            setError(err.response?.data?.message || "Failed to submit reservation request.");
        } finally {
            setIsSubmitting(false);
        }
    };

    const content = (
        <div className={isInline ? styles.inlineContainer : styles.modal}>
            {!isInline && <button className={styles.closeButton} onClick={onClose}>&times;</button>}
            <h2>{isInline ? 'Submit Reservation Request' : 'Reservation Request'}</h2>
            {selectedDates.length > 0 && (
                <p>Requesting for: <strong>{selectedDates.length === 1 ? selectedDates[0] : `${selectedDates[0]} to ${selectedDates[selectedDates.length - 1]}`}</strong></p>
            )}

            {error && <div className={styles.error}>{error}</div>}

            <form onSubmit={handleSubmit} className={styles.form}>

                {/* 1. Name of person initiating reservation */}
                <div className={styles.section}>
                    <h3>1. Name of Person Initiating Reservation</h3>
                    <input
                        type="text"
                        name="guestName"
                        placeholder="Full Name *"
                        required
                        value={formData.guestName}
                        onChange={handleChange}
                        className={styles.fullWidthInput}
                    />

                    <div className={styles.subSection}>
                        <h4>a) Contact Information</h4>
                        <div className={styles.row}>
                            <input type="email" name="guestEmail" placeholder="Email Address *" required value={formData.guestEmail} onChange={handleChange} />
                            <input type="tel" name="guestPhone" placeholder="Cell Phone *" required value={formData.guestPhone} onChange={handleChange} />
                        </div>
                    </div>
                </div>

                {/* 2. Name of Church */}
                <div className={styles.section}>
                    <h3>2. Name of Church</h3>
                    <select name="accountId" value={formData.accountId} onChange={handleChange} required className={styles.fullWidthInput}>
                        <option value="">Select Church / Organization *</option>
                        {accounts.map(acc => (
                            <option key={acc.id} value={acc.id}>{acc.accountName}</option>
                        ))}
                    </select>
                </div>

                {/* 3. Description of Group */}
                <div className={styles.section}>
                    <h3>3. Description of Group</h3>
                    <p className={styles.helperText}>(i.e., College or District retreat; Service Group, etc.)</p>
                    <input
                        type="text"
                        name="groupDescription"
                        placeholder="Group Description (i.e., College or District retreat; Service Group, etc.)"
                        value={formData.groupDescription}
                        onChange={handleChange}
                        className={styles.fullWidthInput}
                    />
                </div>

                {/* 4. Purpose of Visit */}
                <div className={styles.section}>
                    <h3>4. Purpose of Highview House Visit</h3>
                    <textarea
                        name="purposeOfVisit"
                        placeholder="Briefly describe the purpose of your visit..."
                        value={formData.purposeOfVisit}
                        onChange={handleChange}
                        className={styles.fullWidthInput}
                    />
                </div>

                {/* Hidden Event Name/Description fields to satisfy backend if needed, or we can repurpose inputs. 
                    I'll keep eventName as a hidden field populated by Group Description if empty, or just adding a small input. 
                    Actually user didn't ask for "Event Name". I'll auto-generate it or just leave it blank if allowed. 
                    Let's Add a "Event Name" field but maybe smaller or under Group Desc. 
                    Wait, looking at the backend requirement assumption, "eventName" is likely required. 
                    I'll add it as a sub-field or just rename "Group Description" to map to it? 
                    Strictly following user request: Item 3 IS "Description of Group". 
                    I will ADD a field for "Event Name" to be safe, but maybe visually integrated. 
                    Actually, I'll repurpose: 
                    I will add a hidden logic: if eventName is empty, maybe set it to "Reservation" on submit? 
                    Let's just add it as a "Event Title" optional field or required if backend needs it. 
                    I'll stick to the user's visible list and maybe handle eventName in the background or add it discreetly.
                    BUT, technically "Description of Group" -> groupDescription. 
                    I'll just add the missing fields and keep "Event Name" but visually consistent.
                    Re-reading: "3. Description of Group...". 
                    I'll add the requested fields. I will also keep "Event Name" but put it in a "Additional Details" or generic section if it doesn't fit the numbered list?
                    The user said "I want it to align *better* with this", implying this IS the structure.
                    I will put "Event Name" inside section 3 or 4, or just rename it. 
                    Let's just add "Event Name" as a field in Section 3 for now to be safe.
                */}

                <div className={styles.section} style={{ display: 'none' }}>
                    {/* HIDDEN: Using a default or derivation for Event Name if possible, or exposing it if I must. 
                        Safe bet: Expose it. 
                        Let's put it back in Section 3 as "Event Title".
                    */}
                    <input type="text" name="eventName" value={formData.eventName} onChange={handleChange} placeholder="Event Name (Internal)" />
                </div>

                {/* 5. Approximate Numbers */}
                <div className={styles.section}>
                    <h3>5. Approximate Number of Attendees</h3>
                    <div className={styles.row}>
                        <label className={styles.numberLabel}>
                            Adults Expected:
                            <input type="number" name="adultCount" min="0" value={formData.adultCount} onChange={handleChange} />
                        </label>
                        <label className={styles.numberLabel}>
                            Children Expected (Under 18):
                            <input type="number" name="childCount" min="0" value={formData.childCount} onChange={handleChange} />
                        </label>
                    </div>
                </div>

                {/* 6. Responsible Person */}
                <div className={styles.section}>
                    <h3>6. Person Responsible for Payment Arrangements</h3>
                    <input
                        type="text"
                        name="responsiblePerson"
                        placeholder="Name of Responsible Person *"
                        required
                        value={formData.responsiblePerson}
                        onChange={handleChange}
                        className={styles.fullWidthInput}
                    />
                    <p className={styles.noteText}><strong>Note:</strong> Expires in {expiryHours} hour{expiryHours !== 1 ? 's' : ''} if no payment is made by your selected local church.</p>
                </div>

                {/* Event Name is likely required. I'll add a hook to set it if empty. */}

                <div className={styles.actions}>
                    <button type="button" onClick={onClose} disabled={isSubmitting} className={styles.cancelBtn}>Cancel</button>
                    <button type="submit" disabled={isSubmitting} className={styles.submitBtn}>
                        {isSubmitting ? 'Submitting...' : 'Submit Request'}
                    </button>
                </div>
            </form>
        </div>
    );

    if (isInline) {
        return content;
    }

    return (
        <div className={styles.overlay}>
            {content}
        </div>
    );
};

export default PublicReservationForm;
